import React, { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  Navigate,
  useLocation,
  useParams,
  useOutletContext,
} from "react-router-dom";

import { AuthProvider, useAuth } from "./Context/AuthContext";
import { useWeddingData } from "./hooks/useWeddingData";

// Company website components
import NavBarComponent from "./NavBarComponent/NavBarComponent";
import HomeComponent from "./HomeComponent/HomeComponent";
import FAQPage from "./FAQPage/FAQPage";
import HelpPage from "./HelpPage/HelpPage";
import FooterComponent from "./FooterComponent/FooterComponent";

// Product components
import WeddingHomeComponent from "./WeddingHomeComponent/WeddingHomeComponent";
import RsvpComponent from "./RsvpComponent/RsvpComponent";
import Dashboard from "./DashboardComponent/Dashboard";
import AddGuestForm from "./AddGuestForm/AddGuestForm";
import PendingGuests from "./PendingGuests/PendingGuests";
import NotComingGuests from "./NotComingGuests/NotComingGuests";
import ChangeGuestState from "./ChangeGuestState/ChangeGuestState";
import AddAllGuests from "./AddAllGuests/AddAllGuests";
import LoginComponent from "./LoginComponent/LoginComponent";
import ReusableGuestList from "./ReusableGuestList/ReusableGuestList";
import LiquorComponent from "./LiquorComponent/LiquorComponent";
import TableSearch from "./TableSearch/TableSearch";
import SignUpComponent from "./SignUpComponent/SignUpComponent";
import AdminLayout from "./AdminLayout/AdminLayout";
import LoaderComponent from "./LoaderComponent/LoaderComponent";
import AdminLoginComponent from "./AdminLoginComponent/AdminLoginComponent";
import AdminDashboard from "./AdminDashboard/AdminDashboard";
import BudgetComponent from "./BudgetComponent/BudgetComponent";
import AgendaEditComponent from "./AgendaEditComponent/AgendaEditComponent";
import ImageGalleryComponent from "./ImageGalleryComponent/ImageGalleryComponent";
import ParticipationConfirmed from "./PariticipationConfirmed/PariticipationConfirmed";
import QrComponent from "./QrComponent/QrComponent";
import ColorComponent from "./ColorComponent/ColorComponent";
import AdminFeatureTogglePanel from "./AdminFeatureTogglePanel/AdminFeatureTogglePanel";
import PaymentComponent from "./PaymentComponent/PaymentComponent";
import SongChangeComponent from "./SongChangeComponent/SongChangeComponent";

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);

  return null;
}

function CompanyLayout() {
  return (
    <>
      <NavBarComponent />
      <Outlet />
    </>
  );
}

function HomePage() {
  return (
    <>
      <HomeComponent />
      <FooterComponent />
    </>
  );
}

function RequireUserAuth({ children }) {
  const { user, loading } = useAuth();

  if (loading) return null;

  return user ? children : <Navigate to="/login" replace />;
}

function RequireAdminAuth({ children }) {
  const { user, loading } = useAuth();

  if (loading) return null;

  return user ? children : <Navigate to="/adminlogin" replace />;
}

function WeddingSiteLayout() {
  const { slug } = useParams();
  const { weddingData, loading } = useWeddingData(slug);

  const bride_name = weddingData?.bride_name || "Bride";
  const groom_name = weddingData?.groom_name || "Groom";

  useEffect(() => {
    if (weddingData) {
      document.title = `${bride_name} & ${groom_name}`;
    }
  }, [weddingData, bride_name, groom_name]);

  if (loading) return <LoaderComponent slug={slug} theme={weddingData} />;

  if (!weddingData) {
    return <div>Wedding site not found.</div>;
  }

  return <Outlet context={{ slug, weddingData }} />;
}

function WeddingSitePage() {
  const { slug, weddingData } = useOutletContext();
  return <WeddingHomeComponent weddingData={weddingData} slug={slug} />;
}

function PublicRsvpPage() {
  const { slug, weddingData } = useOutletContext();
  return <RsvpComponent slug={slug} weddingData={weddingData} />;
}

function PublicTableSearchPage() {
  const { slug, weddingData } = useOutletContext();
  return <TableSearch slug={slug} weddingData={weddingData} />;
}

function GroomListPage() {
  const { weddingData, slug } = useOutletContext();
  const groom_name = weddingData?.groom_name || "Groom";

  return (
    <ReusableGuestList
      title={`${groom_name}'s Guest List`}
      guestType={`${groom_name}'s Guest`}
      collectionId="groom"
      pageSize={10}
      backTo={`/sites/${slug}/dashboard`}
    />
  );
}

function BrideListPage() {
  const { weddingData, slug } = useOutletContext();
  const bride_name = weddingData?.bride_name || "Bride";

  return (
    <ReusableGuestList
      title={`${bride_name}'s Guest List`}
      guestType={`${bride_name}'s Guest`}
      collectionId="bride"
      pageSize={10}
      backTo={`/sites/${slug}/dashboard`}
    />
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />

        <Routes>
          {/* Company website */}
          <Route element={<CompanyLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/login" element={<LoginComponent />} />
            <Route path="/signup" element={<SignUpComponent />} />
            <Route path="/adminlogin" element={<AdminLoginComponent />} />
          </Route>

          {/* Global admin route */}
          <Route
            path="/admindashboard"
            element={
              <RequireAdminAuth>
                <AdminDashboard />
              </RequireAdminAuth>
            }
          />

          {/* Wedding public pages */}
          <Route path="/sites/:slug" element={<WeddingSiteLayout />}>
            <Route index element={<WeddingSitePage />} />
            <Route path="rsvp" element={<PublicRsvpPage />} />
            <Route path="tablesearch" element={<PublicTableSearchPage />} />

            {/* Wedding user protected pages */}
            <Route
              element={
                <RequireUserAuth>
                  <AdminLayout />
                </RequireUserAuth>
              }
            >
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="addguest" element={<AddGuestForm />} />
              <Route path="groom-list" element={<GroomListPage />} />
              <Route path="bride-list" element={<BrideListPage />} />
              <Route path="addallguests" element={<AddAllGuests />} />
              <Route path="pendingguests" element={<PendingGuests />} />
              <Route path="notcomingguests" element={<NotComingGuests />} />
              <Route path="changegueststate" element={<ChangeGuestState />} />
              <Route path="liquorcount" element={<LiquorComponent />} />
              <Route path="budgetcalculation" element={<BudgetComponent />} />
              <Route path="edit-agenda" element={<AgendaEditComponent />} />
              <Route path="image-gallery" element={<ImageGalleryComponent />} />
              <Route path="site-colors" element={<ColorComponent />} />
              <Route path="change-music" element={<SongChangeComponent />} />
              <Route path="billing-payments" element={<PaymentComponent/>} />
              <Route
                path="show-hide-sections"
                element={<AdminFeatureTogglePanel />}
              />
              <Route
                path="participationconfirmed"
                element={<ParticipationConfirmed />}
              />
              <Route path="qrcode" element={<QrComponent />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}